import client from "./axios";

const ENV = `${process.env.REACT_APP_BACKEND_URL}/instagram-business`;

/**
 * Fetch the normalised Instagram Business comments for the logged-in user.
 * @returns API payload `{ data: Comment[] }`
 */
export const getInstagramComments = async () => {
  const response = await client.get(`${ENV}/comments`);
  return response.data;
};

/**
 * Delete a comment: removes it on Instagram (Graph API) and soft-deletes the
 * stored record (status -> "deleted"). Pass the Mongo `_id` or the `comment_id`.
 * @returns API payload `{ success, igDeleted, data }`
 */
export const deleteInstagramComment = async (id: string) => {
  const response = await client.delete(`${ENV}/comments/${id}`);
  return response.data;
};

/**
 * Reconcile stored comments with Instagram: marks comments that were deleted
 * on Instagram (no webhook fires for deletions) as "deleted" in the DB.
 * @returns API payload `{ success, deletedCount, data: Comment[] }`
 */
export const syncInstagramComments = async () => {
  const response = await client.post(`${ENV}/comments/sync`);
  return response.data;
};

/**
 * Comment on a media the business is mentioned/tagged in (Mentions tab, media
 * owned by another account). Posts via the Instagram mentions edge and stores the
 * comment so it appears in the Comments tab.
 * @returns API payload `{ data: Comment }`
 */
export const createMentionComment = async (
  media_id: string,
  message: string,
) => {
  const response = await client.post(`${ENV}/mentions/comment`, {
    media_id,
    message,
  });
  return response.data;
};

/**
 * Reply to a specific comment (Comments tab). Posts the reply to Instagram and
 * stores it threaded under the parent comment. Pass the parent's `_id` or
 * `comment_id`.
 * @returns API payload `{ data: Comment }`
 */
export const replyToComment = async (id: string, message: string) => {
  const response = await client.post(`${ENV}/comments/${id}/reply`, { message });
  return response.data;
};

/**
 * Hide or unhide a comment on Instagram. Only works for comments on media you
 * own (mention/tagged posts can't be hidden). Pass the `_id` or `comment_id`.
 * @returns API payload `{ success, data: Comment }`
 */
export const setCommentHidden = async (id: string, hide: boolean) => {
  const response = await client.post(`${ENV}/comments/${id}/hide`, { hide });
  return response.data;
};
